import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { ErrorBoundary } from '@/components/ui/error-boundary'
import { LoadFailedNotice } from '@/components/ui/load-failed-notice'
import type { MidiController } from '@/hooks/use-midi'
import type { Fm1Colorway } from '@/lib/fm1-colorway'
import { hasSeenHelp } from '@/lib/help-seen'
import { sharedPatchPayload } from '@/lib/share-link-fragment'

// The guide loads when it is first asked for; the trigger stays in the initial bundle.
const HelpDialog = lazy(() =>
  import('@/components/help-dialog').then((module) => ({ default: module.HelpDialog })),
)

type HelpButtonProps = {
  colorway: Fm1Colorway
  midi: Pick<MidiController, 'firmware'>
}

export function HelpButton({ colorway, midi }: HelpButtonProps) {
  const { t } = useTranslation()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [requested, setRequested] = useState(false)
  // A guide that opened itself greets a first visit; one asked for opens on the full guide.
  const [firstVisit, setFirstVisit] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)

  // A first visit opens the guide itself, before anything has been clicked, unless it came to open a
  // share link, whose patch opens instead.
  useEffect(() => {
    if (!hasSeenHelp() && sharedPatchPayload(window.location.hash) === null) {
      setFirstVisit(true)
      setRequested(true)
    }
  }, [])

  return (
    <>
      <Button
        aria-label={t('help.open')}
        className="group/help"
        onClick={() => {
          setLoadFailed(false)
          setFirstVisit(false)
          setRequested(true)
        }}
        ref={triggerRef}
        size="bare"
        title={t('help.open')}
        type="button"
        variant="bare"
      >
        <span
          aria-hidden="true"
          className="hero-action font-dot-matrix grid size-[26px] place-items-center text-base leading-none font-bold text-[var(--crt-acc-lt)] group-hover/help:bg-[var(--crt-sel-bg)]"
        >
          ?
        </span>
      </Button>
      {loadFailed ? <LoadFailedNotice message={t('ui.helpOpenFailed')} /> : null}
      {requested ? (
        <ErrorBoundary
          onError={() => {
            setRequested(false)
            setLoadFailed(true)
          }}
        >
          <Suspense fallback={null}>
            <HelpDialog
              colorway={colorway}
              firstVisit={firstVisit}
              midi={midi}
              onClose={() => {
                setRequested(false)
                triggerRef.current?.focus()
              }}
            />
          </Suspense>
        </ErrorBoundary>
      ) : null}
    </>
  )
}
